import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const eventFaqs = [
  {
    q: 'How much does it cost to host an e-waste collection event?',
    a: 'Our e-waste collection events are $200 per hour with a 3-hour minimum ($600 total). This includes the truck, 2 certified technicians, collection bins, all supplies, gas, and insurance. There are no hidden fees.',
  },
  {
    q: 'What types of electronics do you accept at collection events?',
    a: 'We accept all types of electronics: CRT and flat-screen TVs, computers, laptops, monitors, printers, phones, tablets, batteries, cables, and small appliances. No sorting is required — our team handles everything on-site.',
  },
  {
    q: 'Do we need to sort the electronics before the event?',
    a: 'No sorting needed at all. Our trained technicians handle all collection, sorting, and transportation. Just let your community know to bring their e-waste, and we do the rest — including secure data destruction for any devices with storage.',
  },
  {
    q: 'How long does a typical collection event last?',
    a: 'Most community events run 3–4 hours. This gives enough time for steady drop-offs while keeping costs manageable. We can extend if needed at $200 per hour. Schools and businesses often book 4-hour blocks for maximum turnout.',
  },
  {
    q: 'What organizations can host an e-waste collection event?',
    a: 'Schools, businesses, hospitals, churches, neighborhood associations, property management companies, and nonprofits can all host events. We also work with municipalities and government agencies for city-wide collection drives.',
  },
];

const EventsJsonLd = () => (
  <>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/events#webpage`,
      "url": `${SITE_URL}/events`,
      "name": "E-Waste Collection Events Los Angeles | Host a Recycling Event | Greenly",
      "description": "Greenly organizes on-site e-waste collection events for schools, businesses, hospitals, churches & neighborhoods across Los Angeles. We handle everything. Call (213) 600-1000.",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "about": { "@id": `${SITE_URL}/#business` },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "E-Waste Events", "item": `${SITE_URL}/events` }
        ]
      }
    })}</script>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Event",
      "name": "E-Waste Collection Event — Los Angeles",
      "url": `${SITE_URL}/events`,
      "organizer": {
        "@type": "Organization",
        "name": "Greenly Electronics Recycling",
        "url": SITE_URL,
        "telephone": "+12136001000"
      },
      "location": {
        "@type": "Place",
        "name": "Your Location — Los Angeles County",
        "address": { "@type": "PostalAddress", "addressLocality": "Los Angeles", "addressRegion": "CA", "addressCountry": "US" }
      },
      "offers": { "@type": "Offer", "price": "200", "priceCurrency": "USD" }
    })}</script>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": eventFaqs.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a,
        },
      })),
    })}</script>
  </>
);

const EventsPage = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <>
      <title>E-Waste Collection Events Los Angeles | Host a Recycling Event | Greenly</title>
      <meta name="description" content="Host an e-waste collection event in Los Angeles. Greenly brings the truck, crew & supplies to schools, businesses, hospitals & neighborhoods. $200/hr. Call (213) 600-1000." />
      <meta name="keywords" content="e-waste collection event Los Angeles, electronics recycling event LA, community e-waste drive" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/events`} />
      <EventsJsonLd />
      <Navbar />
      <main>

        {/* ── HERO — dark green, split layout matching reference ── */}
        <section className="pt-16 bg-[#1a4a2e] min-h-[480px] flex items-center">
          <div className="max-w-7xl mx-auto px-4 md:px-8 w-full py-14 md:py-16">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">

              {/* Left */}
              <div className="w-full lg:w-1/2 text-white">
                {/* pill badge */}
                <div className="inline-flex items-center gap-2 border border-white/20 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
                  <i className="ri-calendar-event-line"></i>
                  Host an E-Waste Event
                </div>

                <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-5">
                  Host an E-Waste Event —{' '}
                  <span className="text-[#6fcf97]">Make an Impact in Your Community</span>
                </h1>

                <p className="text-white/80 text-base leading-relaxed mb-4">
                  Turn your workplace or community event into something meaningful. We make it simple by providing the truck, collection bins, and expert team to handle everything from start to finish — including secure data destruction.
                </p>
                <p className="text-white/80 text-base leading-relaxed mb-8">
                  But it doesn&apos;t stop there. Usable devices are refurbished and placed into the hands of seniors and underserved families through{' '}
                  <strong className="text-white">Bridge The Digital Divide</strong>, helping close the digital gap right here in our community.
                </p>

                {/* 3 icon badges */}
                <div className="flex flex-wrap gap-3 mb-8">
                  {[
                    { icon: 'ri-recycle-line', label: 'Reduce Waste' },
                    { icon: 'ri-lock-password-line', label: 'Protect Data' },
                    { icon: 'ri-heart-line', label: 'Empower Lives' },
                  ].map((b) => (
                    <div key={b.label} className="flex flex-col items-center gap-2 bg-white/10 border border-white/15 rounded-xl px-6 py-4 min-w-[100px]">
                      <div className="w-9 h-9 flex items-center justify-center bg-white/15 rounded-full">
                        <i className={`${b.icon} text-white text-lg`}></i>
                      </div>
                      <span className="text-white text-xs font-semibold">{b.label}</span>
                    </div>
                  ))}
                </div>

                {/* CTA buttons */}
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/reserve-date"
                    className="inline-flex items-center gap-2 bg-white text-[#1a4a2e] font-bold px-6 py-3 rounded-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-calendar-check-line"></i>
                    Click Here to Reserve a Date
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <a
                    href="tel:+12136001000"
                    className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-phone-line"></i>
                    (213) 600-1000
                  </a>
                </div>
              </div>

              {/* Right — truck image + floating card */}
              <div className="w-full lg:w-1/2 relative">
                <div className="w-full h-[300px] md:h-[380px] rounded-2xl overflow-hidden">
                  <img
                    src="/images/greenlyelectronicsrecycling.png"
                    alt="Greenly Electronics Recycling truck for e-waste collection events Los Angeles"
                    className="w-full h-full object-cover object-center"
                    loading="eager"
                  />
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Who We Serve + How It Works — side by side */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:justify-center">

              {/* Who We Serve */}
              <div className="w-full lg:w-auto lg:min-w-[260px]">
                <h2 className="text-lg font-extrabold text-gray-900 mb-4">Who We Serve</h2>
                <ul className="space-y-2">
                  {[
                    { icon: 'ri-building-2-line', label: 'Businesses & Corporations' },
                    { icon: 'ri-home-8-line', label: 'Property Management & HOAs' },
                    { icon: 'ri-graduation-cap-line', label: 'Schools & Universities' },
                    { icon: 'ri-hospital-line', label: 'Hospitals & Medical Facilities' },
                    { icon: 'ri-heart-line', label: 'Churches & Nonprofits' },
                    { icon: 'ri-community-line', label: 'Neighborhood Associations' },
                  ].map((org) => (
                    <li key={org.label} className="flex items-center gap-3">
                      <div className="w-6 h-6 flex items-center justify-center shrink-0">
                        <i className={`${org.icon} text-[#1B6B3A] text-base`}></i>
                      </div>
                      <span className="text-sm text-gray-700">{org.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Divider */}
              <div className="hidden lg:block w-px bg-gray-200 self-stretch"></div>

              {/* How It Works */}
              <div className="w-full lg:w-auto lg:min-w-[320px]">
                <h2 className="text-lg font-extrabold text-gray-900 mb-4">How It Works</h2>
                <ul className="space-y-3">
                  {[
                    { num: '1', title: 'Call or Request Online', desc: 'Call (213) 600-1000 or fill out the form below.' },
                    { num: '2', title: 'We Plan Together', desc: 'We confirm date, location, and any special needs.' },
                    { num: '3', title: 'We Show Up & Handle It', desc: 'Our truck and crew handle all collection and sorting.' },
                    { num: '4', title: 'Get Your Certificate', desc: 'Receive a Certificate of Recycling after the event.' },
                    { num: '5', title: 'Simple Pricing', desc: '$200/hr · 3-hour minimum ($600) · Includes truck, 2 techs, supplies, gas & insurance.' },
                  ].map((step) => (
                    <li key={step.num} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-[#1B6B3A] rounded-full shrink-0 mt-0.5">
                        <span className="text-white text-xs font-bold">{step.num}</span>
                      </div>
                      <div>
                        <span className="text-sm font-bold text-gray-900">{step.title}</span>
                        <span className="text-sm text-gray-500"> — {step.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          </div>
        </section>

        {/* ── FAQ Section ── */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Common Questions</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                E-Waste Collection Event FAQs
              </h2>
            </div>
            <div className="space-y-3">
              {eventFaqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIdx(openIdx === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer"
                  >
                    <h4 className="font-extrabold text-gray-900 text-sm">{faq.q}</h4>
                    <div className="w-8 h-8 flex items-center justify-center bg-[#E8F5EE] rounded-full shrink-0">
                      <i className={`ri-arrow-down-s-line text-[#1B6B3A] transition-transform duration-200 ${openIdx === i ? 'rotate-180' : ''}`}></i>
                    </div>
                  </button>
                  {openIdx === i && (
                    <div className="px-5 pb-5">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
      <Footer />
      <BackToTop />
    </>
  );
};

export default EventsPage;